import {
  Box,
  Flex,
  Table as ChakraTable,
  TableContainer,
  TableContainerProps,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import { Divider } from '@main/complykit/components/divider';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getGroupedRowModel,
  Table as TanstackTable,
  useReactTable,
} from '@tanstack/react-table';
import { ReactNode } from 'react';

import { GlobalFilter } from './filters/global-filter';
import { useTableSearchQuery } from './use-table-search-query';

type Extract<Type> = Type extends string ? Type : never;

interface GroupedTableProps<TData> extends TableContainerProps {
  data: TData[];
  columns: ColumnDef<TData>[];
  entityName: string;
  groupBy: Extract<keyof TData>;
  renderGroupDescription?: (item: TData) => ReactNode;
}

export const GroupedTable = <TData,>({
  data,
  columns,
  entityName,
  groupBy,
  renderGroupDescription,
  ...rest
}: GroupedTableProps<TData>) => {
  const [globalFilter, setGlobalFilter] = useTableSearchQuery({
    searchParam: `${entityName}Search`,
  });

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: 'includesString',
    state: {
      globalFilter,
    },
    initialState: {
      columnVisibility: {
        [groupBy]: false,
      },
      grouping: [groupBy],
      expanded: true,
    },
  });

  const tableBorderColor = useColorModeValue('gray.200', 'whiteAlpha.300');

  return (
    <TableContainer
      border="1px"
      borderColor={tableBorderColor}
      rounded="md"
      overflow="auto"
      {...rest}
      minW={rest.minW || 'full'}
    >
      <Box w="fit-content">
        <TableFilter table={table} />
        <ChakraTable aria-label={entityName} variant="grouped" size="sm">
          <TableHeader table={table} />
          <TableBody table={table} renderGroupDescription={renderGroupDescription} />
        </ChakraTable>
      </Box>
    </TableContainer>
  );
};

function TableFilter<TData>({ table }: { table: TanstackTable<TData> }) {
  return (
    <Box minW="full" w="full">
      <Flex justifyContent={['start', 'end']} py={3} px={[2, 6]}>
        <Box w="xs">
          <GlobalFilter table={table} />
        </Box>
      </Flex>
      <Divider />
    </Box>
  );
}

function TableHeader<TData>({ table }: { table: TanstackTable<TData> }) {
  return (
    <Thead>
      {table.getHeaderGroups().map((headerGroup) => (
        <Tr key={headerGroup.id}>
          {headerGroup.headers.map((header, headerIndex) => {
            const width = header.column.getSize();
            return (
              <Th
                sx={{
                  width: headerIndex === 0 ? width * 2 : width,
                }}
                key={header.id + headerIndex}
                colSpan={headerIndex === 0 ? 2 : 1}
              >
                {header.isPlaceholder ? null : (
                  <>{flexRender(header.column.columnDef.header, header.getContext())}</>
                )}
              </Th>
            );
          })}
        </Tr>
      ))}
    </Thead>
  );
}

function TableBody<TData>({
  table,
  renderGroupDescription,
}: {
  table: TanstackTable<TData>;
  renderGroupDescription?: (item: TData) => ReactNode;
}) {
  const groupTitleBgColor = useColorModeValue('gray.25', 'gray.800');
  const groupTitleBorderColor = useColorModeValue('gray.200', 'whiteAlpha.300');

  return (
    <Tbody>
      {table.getRowModel().rows.map((row) => {
        if (row.subRows.length !== 0) {
          const description = renderGroupDescription?.(row.original);
          return (
            <Tr
              key={row.id}
              backgroundColor={groupTitleBgColor}
              borderTop="1px"
              borderBottom="1px"
              borderColor={groupTitleBorderColor}
            >
              <Td
                py={3}
                colSpan={row.getAllCells().length}
                fontWeight="semibold"
                textColor="gray.500"
                lineHeight="6"
                whiteSpace={description ? 'normal' : undefined}
              >
                {row.groupingValue as string}
                {description}
              </Td>
            </Tr>
          );
        }

        return (
          <Tr key={row.id} h={12}>
            {row.getVisibleCells().map((cell, cellIndex) => {
              return (
                <Td
                  key={cell.id}
                  colSpan={cellIndex === 0 ? 2 : 1}
                  overflowX="clip"
                  fontWeight="medium"
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Td>
              );
            })}
          </Tr>
        );
      })}
    </Tbody>
  );
}
